import { generateInvoiceHTML, INVOICE_COPY_TYPE } from './InvoiceTemplates/InvoiceGenerator'
import { getSalesOrderHelper } from '../../helper/home-helper'
import Config from '../../config/config'

/**
 * Print Customer Copy of the invoice
 * @param {string} invoiceId - The sales order ID
 * @param {string} servedByName - Name of the user who served the customer
 * @param {string} pharmacyName - Name of the pharmacy (from loginData)
 * @returns {Promise<boolean>} - Returns true if printing was successful
 */
export const handlePrintInvoice = async (invoiceId, servedByName = null, pharmacyName = null) => {
  console.log('handlePrintInvoice called with ID:', invoiceId)
  
  try {
    const invoiceData = await getSalesOrderHelper(invoiceId)
    console.log('Invoice data received:', invoiceData)

    if (!invoiceData) {
      console.error('Invoice data not found')
      return false
    }

    const branchData = invoiceData.branch_details || invoiceData.branch || {}
    console.log('Branch data:', branchData)

    // Add served by name to invoice data if provided
    if (servedByName) {
      invoiceData.served_by = servedByName
    }

    // Add pharmacy name to branch data if provided (from loginData)
    if (pharmacyName) {
      branchData.pharmacy_name = pharmacyName
    }

    // Transform logo URL to full URL
    if (branchData.logo) {
      branchData.logo = Config.getFullImageUrl(branchData.logo)
    }

    // Print customer copy only
    printCustomerCopy(invoiceData, branchData)

    return true
  } catch (error) {
    console.error('Error in handlePrintInvoice:', error)
    throw error
  }
}

/**
 * Print a single copy of the invoice
 * @param {string} invoiceId - The sales order ID
 * @param {string} copyType - PHARMACY_COPY or CUSTOMER_COPY
 * @param {string} pharmacyName - Name of the pharmacy (from loginData)
 * @returns {Promise<boolean>}
 */
export const handlePrintSingleCopy = async (invoiceId, copyType, pharmacyName = null) => {
  const invoiceData = await getSalesOrderHelper(invoiceId)

  if (!invoiceData) {
    console.error('Invoice data not found')
    return false
  }

  const branchData = invoiceData.branch_details || invoiceData.branch || {}

  // Add pharmacy name to branch data if provided (from loginData)
  if (pharmacyName) {
    branchData.pharmacy_name = pharmacyName
  }

  // Transform logo URL to full URL
  if (branchData.logo) {
    branchData.logo = Config.getFullImageUrl(branchData.logo)
  }

  await printInvoiceCopy(invoiceData, branchData, copyType)

  return true
}

/**
 * Print Customer Copy only
 */
const printCustomerCopy = (invoiceData, branchData) => {
  const customerHTML = generateInvoiceHTML(invoiceData, branchData, INVOICE_COPY_TYPE.CUSTOMER_COPY)

  // Open print window
  const printWindow = window.open('', '_blank')

  if (!printWindow) {
    alert('Please allow popups for this site to enable printing')
    return
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Invoice - ${invoiceData.invoice_number}</title>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Poppins', 'Arial', sans-serif;
            background: white;
          }
          .customer-copy-page {
            max-width: 300px;
            margin: 0 auto;
            padding: 10px;
          }
          @media print {
            @page {
              size: 80mm auto;
              margin: 0;
            }
            body {
              margin: 0;
              padding: 0;
            }
            .customer-copy-page {
              page-break-inside: avoid;
            }
          }
          @media screen {
            .customer-copy-page {
              border: 1px solid #ccc;
              margin: 20px auto;
            }
          }
        </style>
      </head>
      <body>
        <div class="customer-copy-page">
          ${customerHTML}
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 1000);
            }, 500);
          };
        </script>
      </body>
    </html>
  `)

  printWindow.document.close()
}

/**
 * Print a single invoice copy
 */
const printInvoiceCopy = async (invoiceData, branchData, copyType) => {
  const invoiceHTML = generateInvoiceHTML(invoiceData, branchData, copyType)
  const copyTitle = copyType === INVOICE_COPY_TYPE.PHARMACY_COPY ? 'Pharmacy Copy' : 'Customer Copy'

  const printWindow = window.open('', '_blank')

  if (!printWindow) {
    alert('Please allow popups for this site to enable printing')
    return
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${copyTitle} - ${invoiceData.invoice_number}</title>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Poppins', 'Arial', sans-serif;
            background: white;
          }
          .invoice-container {
            max-width: 300px;
            margin: 0 auto;
            padding: 10px;
          }
          @media print {
            @page {
              size: 80mm auto;
              margin: 0;
            }
            body {
              margin: 0;
              padding: 0;
            }
          }
          @media screen {
            .invoice-container {
              border: 1px solid #ccc;
              margin: 20px auto;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          ${invoiceHTML}
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 1000);
            }, 500);
          };
        </script>
      </body>
    </html>
  `)

  printWindow.document.close()
}

/**
 * Print invoice directly from invoice data (no API call)
 * Useful when you already have the invoice data
 * @param {string} pharmacyName - Name of the pharmacy (from loginData)
 */
export const printInvoiceFromData = (invoiceData, copyType = INVOICE_COPY_TYPE.CUSTOMER_COPY, pharmacyName = null) => {
  const branchData = invoiceData.branch_details || invoiceData.branch || {}
  
  // Add pharmacy name to branch data if provided (from loginData)
  if (pharmacyName) {
    branchData.pharmacy_name = pharmacyName
  }

  // Transform logo URL to full URL
  if (branchData.logo) {
    branchData.logo = Config.getFullImageUrl(branchData.logo)
  }
  
  printInvoiceCopy(invoiceData, branchData, copyType)
}

export { INVOICE_COPY_TYPE }
